/**
 * html2canvas webpack alias 懒加载入口
 *
 * webpack.config.js 中配置了：
 *   'html2canvas$'  → 本文件（所有业务代码 import html2canvas 自动走这里）
 *   'html2canvas-original$' → node_modules/html2canvas/dist/html2canvas.esm.js
 *
 * 效果：
 * - 任何地方的 `import html2canvas from 'html2canvas'` 自动变成懒加载，无需修改业务代码
 * - html2canvas 被拆到独立的 "html2canvas" chunk，不打入主包
 * - 新同事再加 import html2canvas 也自动享受懒加载，无维护负担
 *
 * 内部用 'html2canvas-original' 动态导入真实包，绕过外层 alias 避免循环引用
 * （与项目中 mobx-react / mobx-react-original 的双 alias 模式完全一致）
 */
import { Toast } from 'antd-mobile';

// 模块级缓存：加载成功后的 html2canvas 函数本身
let html2canvasModule = null;
// 模块级缓存：进行中的 Promise，保证并发调用只发一次网络请求
let html2canvasPromise = null;

/**
 * 懒加载 html2canvas（幂等）。
 * - 已加载：同步返回缓存 Promise（立即 resolve）
 * - 加载中：复用同一个 Promise
 * - 未加载：发起动态 import，打进独立 "html2canvas" chunk
 */
export const loadHtml2canvas = () => {
  if (html2canvasModule) {
    return Promise.resolve(html2canvasModule);
  }

  if (!html2canvasPromise) {
    html2canvasPromise = import(/* webpackChunkName: "html2canvas" */ 'html2canvas-original')
      .then((module) => {
        html2canvasModule = module.default || module;
        return html2canvasModule;
      })
      .catch((error) => {
        // 失败时重置，允许后续重试
        html2canvasPromise = null;
        console.error('Failed to load html2canvas chunk:', error);
        throw error;
      });
  }

  return html2canvasPromise;
};

/**
 * drop-in 替代：签名与原始 html2canvas(element, options) 完全一致，
 * 返回同样的 Promise<HTMLCanvasElement>。
 * 业务代码调用方式无需任何修改。
 *
 * 说明：懒加载后，chunk 加载失败会以 Promise reject 形式抛出。
 * 现有大量调用点是 `html2canvas(...).then(...)` 且未接 .catch，弱网首次生成海报失败时
 * 用户无任何感知。这里在包装层统一兜底：加载失败先给用户一个 Toast 提示，再原样 reject，
 * 保证既有 .then 回调不会拿到 undefined canvas 继续执行（如 toDataURL 报错），语义不变。
 */
const html2canvasLazy = (element, options) =>
  loadHtml2canvas()
    // 仅兜底「chunk 加载失败」这一新增失败态：提示用户后原样 reject
    .catch((error) => {
      Toast.info('图片生成失败，请重试');
      // chunk加载失败后 throw error的作用
      // 1、下面的then不会执行，避免硬走到then，变成了undefined(element, options)
      // 2、把错误继续抛给调用方，最终返回的是Promise rejected，调用点 html2canvas(...).then(canvas => ...) 的成功回调也不会执行，不会拿到坏的 canvas 继续 toDataURL() 之类的操作
      throw error;
    })
    // 渲染阶段的失败保持原样向调用方抛出，不在此拦截（避免与调用点已有的错误处理重复提示）
    .then((html2canvas) => html2canvas(element, options));

export default html2canvasLazy;



// WEBPACK FOOTER //
// ./src/utils/lazyLoad/html2canvasAlias.js